import { Grid, InputAdornment } from '@mui/material';
import { OfferUiProps } from 'common/contracts';
import { EAssetType } from 'constants/enums';
import { useUploads } from 'hooks/useUpload';
import { defaultBackgroundImage } from 'utils/selectOptionsUtils';

import AcCard from 'components/AcCard/AcCard';
import AcGradientInput from 'components/AcGradientInput/AcGradientInput';
import AcInput from 'components/AcInput/AcInput';
import AcSelect from 'components/AcSelect/AcSelect';
import { EAcSelectItemRenderType } from 'components/AcSelect/AcSelect.types';
import AcSolidInput from 'components/AcSolidInput/AcSolidInput';

const ProgressBarUiForm: React.FC<OfferUiProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  addPictureInputRef,
  getImages,
  setCurrentUploadingField,
  currentUploadingField,
  dup
}) => {
  const { uploadImage, getAssetUploadMessage } = useUploads();

  return (
    <>
      <AcCard
        stackContainer={false}
        title="Progress Bar Frame"
        description="Set your frame settings."
      >
        <Grid container rowSpacing={2} columnSpacing={2}>
          <Grid item xs={6}>
            <AcInput
              header="Background Opacity"
              name="progressBar.backgroundOpacity"
              value={Number(values.progressBar?.backgroundOpacity)}
              onChange={(e) => {
                setFieldValue(
                  'progressBar.backgroundOpacity',
                  Number(e.target.value)
                );
              }}
              onBlur={handleBlur}
              type="number"
              slotProps={{
                htmlInput: {
                  min: 0,
                  max: 100
                },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">%</InputAdornment>
                  )
                }
              }}
              required
              error={
                touched.progressBar?.backgroundOpacity &&
                Boolean(errors?.progressBar?.backgroundOpacity)
              }
              helperText={
                touched.progressBar?.backgroundOpacity
                  ? errors?.progressBar?.backgroundOpacity?.toString()
                  : ''
              }
            />
          </Grid>
          <Grid item xs={6}>
            <AcInput
              header="Frame Radius"
              name="progressBar.frameRadius"
              type="number"
              required
              value={values.progressBar?.frameRadius}
              onChange={handleChange}
              onBlur={handleBlur}
              error={
                touched.progressBar?.frameRadius &&
                Boolean(errors?.progressBar?.frameRadius)
              }
              helperText={
                touched?.progressBar?.frameRadius
                  ? errors?.progressBar?.frameRadius?.toString()
                  : ''
              }
            />
          </Grid>
        </Grid>
      </AcCard>
      <AcCard
        stackContainer={false}
        title="Progress Bar Design"
        description="Set colors and upload assets for your bar."
        className="mt-5"
      >
        <Grid container rowSpacing={2} columnSpacing={2}>
          <Grid item xs={4}>
            <AcGradientInput
              header="Bar Fill Color"
              name="progressBar.barFillColor"
              defaultValue={values.progressBar?.barFillColor}
              onChange={handleChange}
              setValue={setFieldValue}
              onBlur={handleBlur}
              error={
                touched.progressBar?.barFillColor &&
                Boolean(errors.progressBar?.barFillColor)
              }
              helperText={
                touched.progressBar?.barFillColor
                  ? errors.progressBar?.barFillColor?.toString()
                  : ''
              }
            />
          </Grid>
          <Grid item xs={4}>
            <AcGradientInput
              header="Background Color"
              name="progressBar.backgroundColor"
              defaultValue={
                values.progressBar?.backgroundColor ?? {
                  colorOne: '#DABA98',
                  colorTwo: '#FDFCF9',
                  gradientDirection: 'to right'
                }
              }
              onChange={handleChange}
              setValue={setFieldValue}
              onBlur={handleBlur}
              error={
                touched.progressBar?.backgroundColor &&
                Boolean(errors.progressBar?.backgroundColor)
              }
              helperText={
                touched.progressBar?.backgroundColor
                  ? errors.progressBar?.backgroundColor?.toString()
                  : ''
              }
            />
          </Grid>
          <Grid item xs={4}>
            <AcGradientInput
              header="Border Color"
              name="borderColor"
              defaultValue={values.borderColor}
              onChange={handleChange}
              setValue={setFieldValue}
              onBlur={handleBlur}
              error={touched.borderColor && Boolean(errors.borderColor)}
              helperText={
                touched.borderColor ? errors.borderColor?.toString() : ''
              }
            />
          </Grid>
          <Grid item xs={6}>
            <AcInput
              header="Bar Radius"
              name="progressBar.barRadius"
              value={values.progressBar?.barRadius}
              type="number"
              onChange={handleChange}
              onBlur={handleBlur}
              error={
                touched.progressBar?.barRadius &&
                Boolean(errors?.progressBar?.barRadius)
              }
              helperText={
                touched?.progressBar?.barRadius
                  ? errors?.progressBar?.barRadius?.toString()
                  : ''
              }
            />
          </Grid>
          <Grid item xs={6}>
            <AcInput
              header="Border Width"
              name="borderWidth"
              value={values.borderWidth}
              type="number"
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.borderWidth && Boolean(errors?.borderWidth)}
              helperText={
                touched?.borderWidth ? errors?.borderWidth?.toString() : ''
              }
            />
          </Grid>
          <Grid item xs={6}>
            <input
              style={{ display: 'none' }}
              type="file"
              onChange={(e) =>
                uploadImage(
                  e,
                  addPictureInputRef.current as HTMLInputElement,
                  'backgroundImage',
                  setFieldValue,
                  setCurrentUploadingField,
                  currentUploadingField
                )
              }
              ref={addPictureInputRef}
              accept="image/*"
            />
            <AcSelect
              required
              header="Accumulated Image"
              name="progressBar.accumulatedItem.imageUrl"
              value={values.progressBar?.accumulatedItem?.imageUrl || ''}
              uploadConfig={{
                onUploadSuccess: async (url: string) => {
                  await getImages.refetch();
                  setFieldValue('progressBar.accumulatedItem.imageUrl', url);
                },
                uploadType: EAssetType.BG_BUNDLE,
                uploadMessage: getAssetUploadMessage(values.offerUiType)
              }}
              renderType={EAcSelectItemRenderType.IMAGE}
              items={[
                defaultBackgroundImage,
                ...getImages.data
                  .filter((p: any) => p.type === EAssetType.BG_BUNDLE)
                  .map((picture: any) => {
                    return {
                      content: picture.name,
                      key: picture.url,
                      value: picture.url,
                      url: picture.url,
                      name: picture.name
                    };
                  })
              ]}
              onChange={handleChange}
              onBlur={handleBlur}
              onClear={() => {
                setFieldValue('progressBar.accumulatedItem.imageUrl', '');
              }}
              error={
                touched?.progressBar?.accumulatedItem?.imageUrl &&
                Boolean(errors?.progressBar?.accumulatedItem?.imageUrl)
              }
              helperText={
                touched?.progressBar?.accumulatedItem?.imageUrl || dup
                  ? errors?.progressBar?.accumulatedItem?.imageUrl?.toString()
                  : ''
              }
              imgHeight={'32'}
            />
          </Grid>
          <Grid item xs={6}>
            <AcSelect
              header="Accumulated Background Image"
              name="progressBar.accumulatedItem.backgroundImageUrl"
              value={values.progressBar?.accumulatedItem?.backgroundImageUrl || ''}
              uploadConfig={{
                onUploadSuccess: async (url: string) => {
                  await getImages.refetch();
                  setFieldValue('progressBar.accumulatedItem.backgroundImageUrl', url);
                },
                uploadType: EAssetType.BG_BUNDLE,
                uploadMessage: getAssetUploadMessage(values.offerUiType)
              }}
              renderType={EAcSelectItemRenderType.IMAGE}
              items={[
                defaultBackgroundImage,
                ...getImages.data
                  .filter((p: any) => p.type === EAssetType.BG_BUNDLE)
                  .map((picture: any) => {
                    return {
                      content: picture.name,
                      key: picture.url,
                      value: picture.url,
                      url: picture.url,
                      name: picture.name
                    };
                  })
              ]}
              onChange={handleChange}
              onBlur={handleBlur}
              onClear={() => {
                setFieldValue('progressBar.accumulatedItem.backgroundImageUrl', '');
              }}
              error={
                touched?.progressBar?.accumulatedItem?.backgroundImageUrl &&
                Boolean(errors?.progressBar?.accumulatedItem?.backgroundImageUrl)
              }
              helperText={
                touched?.progressBar?.accumulatedItem?.backgroundImageUrl || dup
                  ? errors?.progressBar?.accumulatedItem?.backgroundImageUrl?.toString()
                  : ''
              }
              imgHeight={'32'}
            />
          </Grid>
          {/* <Grid item xs={6}>
            <AcGradientInput
              header="Points Ribbon Color"
              name="progressBar.accumulatedItem.backgroundColor"
              defaultValue={values.progressBar?.accumulatedItem?.backgroundColor}
              onChange={handleChange}
              setValue={setFieldValue}
              onBlur={handleBlur}
              error={
                touched?.progressBar?.accumulatedItem?.backgroundColor &&
                Boolean(errors?.progressBar?.accumulatedItem?.backgroundColor)
              }
              helperText={
                touched?.progressBar?.accumulatedItem?.backgroundColor || dup
                  ? errors?.progressBar?.accumulatedItem?.backgroundColor?.toString()
                  : ''
              }
            />
          </Grid> */}
          <Grid item xs={6}>
            <AcSolidInput
              header="Text Color"
              name="progressBar.textColor"
              defaultValue={values.progressBar?.textColor}
              setValue={setFieldValue}
              onBlur={handleBlur}
            />
          </Grid>
        </Grid>
      </AcCard>
    </>
  );
};

export default ProgressBarUiForm;
